import { get, isNil } from "lodash";
import { rApp, rTranslations } from "./recoil";

import { parseBoolean } from "./utils";
import { useRecoilValue } from "recoil";

export const getValidationObject = (fields) => {
  let obj = {};

  fields
    .filter((f) => f.active !== false)
    .filter((f) => f.componentId !== "Hidden")
    .forEach((field) => {
      const fieldKeyOrId = field.key || field.id;
      const fieldValidation = getFieldValidation(field);
      if (Object.keys(fieldValidation).length > 0) {
        obj[fieldKeyOrId] = fieldValidation;
      }
    });

  return obj;
};

export const getFieldValidation = (field) => {
  let fieldObj = {};

  const validations = [
    "isPrice",
    "isAlphabetical",
    "isNumber",
    "isEmail",
    "isTrue",
    "required",
    "minOffset",
    "maxOffset",
    "customRegex",
    "customRegexError",
  ];

  const isBoolean = ["Switch", "Checkbox"].includes(field.componentId);

  const fieldType = get(field, "type", null);

  // Handle integer
  if (fieldType === "integer") {
    fieldObj["isNumber"] = true;
  }

  // Handle float type
  if (fieldType === "float") {
    fieldObj["isNumber"] = true;
  }

  if (fieldType === "date") {
    // TODO - Validate dates?
    // fieldObj["isDate"] = true;
  }

  if (!field.disabled) {
    validations.forEach((validation) => {
      if (field[validation]) {
        fieldObj[validation] = field[validation];
      }
    });
    const required = field.required !== false;

    if (required && !isBoolean) {
      fieldObj["required"] = required;
    }
  }

  return fieldObj;
};

// Function to validate European decimal format
const isEuroDecimal = (value) => {
  // Updated regex to match the specified criteria
  const euroDecimalRegex =
    /^(?:[₦¥€£$₹]?\d{1,3}(?:\.\d{3})*(?:,\d+)?|\d+(?:,\d+)?)$/;
  return euroDecimalRegex.test(value);
};

const useValidateFields = () => {
  const translations = useRecoilValue(rTranslations);
  const app = useRecoilValue(rApp);

  const numberFormat = get(app, "number_format");

  const numberFormatIsEuro = numberFormat === "euro";

  const validateFields = ({
    validationSchema = {},
    values = {},
    hasSubmitted = true,
    setErrors = null,
    dataSourceConfig = {},
  }) => {
    const errors = {};

    if (!hasSubmitted) {
      return {};
    }

    for (const [field, rules] of Object.entries(validationSchema)) {
      const value = get(values, field);

      const fieldConfig = get(dataSourceConfig, field, {});
      const fieldDataType = get(fieldConfig, "type", null);

      if (!field || !rules) {
        continue;
      }

      function sanitizeRegexInput(input) {
        // Remove the leading and trailing forward slashes, if present
        return input.replace(/^\/|\/$/g, "");
      }

      const isFloat = fieldDataType === "float";
      const isInt = fieldDataType === "integer";
      const isIntOrFloat = isInt || isFloat;

      // Check if the field is alphabetical (only letters and spaces)
      const customRegex = get(rules, "customRegex");
      if (customRegex) {
        const sanitizedInput = sanitizeRegexInput(customRegex);
        const regex = new RegExp(sanitizedInput);

        if (!regex.test(value)) {
          errors[field] = get(rules, "customRegexError", "Custom regex failed");
          continue;
        }
      }

      if (rules.isTrue && !parseBoolean(value)) {
        errors[field] = get(
          translations,
          "validationChecked",
          "This field must be checked."
        );

        continue;
      }

      // Check required field
      if (rules.required && (isNil(value) || value === "")) {
        errors[field] = get(
          translations,
          "validationRequired",
          "This field is required."
        );
        continue;
      }

      // Skip other validations if the field is not required and is empty
      if (!rules.required && (!value || value === "")) {
        continue;
      }

      // Check if the field is a valid number (float or integer)
      if (rules.isNumber) {
        if (numberFormatIsEuro) {
          // European number
          if (!isEuroDecimal(value)) {
            errors[field] = get(
              translations,
              "fieldInvalidPrice",
              "Invalid European price format."
            );
            continue;
          }
        } else {
          // Regular number
          if (!/^\d*\.?\d+$/.test(value)) {
            errors[field] = get(
              translations,
              "validationInvalidNumber",
              "Invalid number format."
            );
            continue;
          }
        }
      }

      // Handle price
      if (rules.isPrice) {
        if (numberFormatIsEuro) {
          // Check European price
          if (!isEuroDecimal(value)) {
            errors[field] = get(
              translations,
              "fieldInvalidPrice",
              "Invalid European price format."
            );
            continue;
          }
        } else {
          // Check regular price
          if (
            !/^(?:[₦¥€£$₹]?\d{1,3}(?:,\d{3})*(?:\.\d{1,2})?|\d+(?:\.\d{1,2})?)$/.test(
              value
            )
          ) {
            errors[field] = get(
              translations,
              "fieldInvalidPrice",
              "Invalid price format."
            );
            continue;
          }
        }
      }

      if (rules.isPrice || rules.isNumber || isIntOrFloat) {
        // When the field is supposed to be a number or price

        const numericValue = parseFloat(value) || isIntOrFloat;

        if (rules.minOffset && numericValue < parseInt(rules.minOffset)) {
          errors[field] = `${get(
            translations,
            "validationMinOffsetNumber",
            "Minimum value should be"
          )} ${rules.minOffset}.`;
          continue;
        }

        if (rules.maxOffset && numericValue > parseInt(rules.maxOffset)) {
          errors[field] = `${get(
            translations,
            "validationMaxOffsetNumber",
            "Maximum value should be"
          )} ${rules.maxOffset}.`;
          continue;
        }
      } else {
        // When the field is treated as text

        if (rules.minOffset && value.length < parseInt(rules.minOffset)) {
          errors[field] = `${get(
            translations,
            "validationMinOffsetText",
            "Minimum characters required"
          )} ${rules.minOffset}.`;
          continue;
        }

        if (rules.maxOffset && value.length > parseInt(rules.maxOffset)) {
          errors[field] = `${get(
            translations,
            "validationMaxOffsetText",
            "Maximum characters required"
          )} ${rules.maxOffset}.`;
          continue;
        }
      }

      // Check if the field is alphabetical (only letters and spaces)
      if (rules.isAlphabetical && !/^[a-zA-Z\s]+$/.test(value)) {
        errors[field] = get(
          translations,
          "valiationInvalidAlphabetical",
          "Only letters and spaces are allowed."
        );
        continue;
      }

      // Check if the field is a valid email
      if (rules.isEmail && !/^[\w\.-]+@[\w\.-]+\.\w+$/.test(value)) {
        errors[field] = get(
          translations,
          "validationInvalidEmail",
          "Invalid email format."
        );
        continue;
      }

      // Default date-based validation
      // if (fieldDataType === "date") {
      // Ensure valid date?
      // if (!isValidDate(value)) {
      //   errors[field] = get(
      //     translations,
      //     "validationInvalidDate",
      //     "Invalid date format."
      //   );
      //   continue;
      // }
      // }
    }

    if (setErrors) {
      setErrors(errors);
    }

    return errors;
  };

  return {
    validateFields,
  };
};

export default useValidateFields;
